export type PersonaSignupAccountType = 'parent' | 'staff';
export type PersonaProvisioningPersona =
  | PersonaSignupAccountType
  | 'administrator';

export type PersonaSignupIntent = {
  version: 1;
  email?: string;
  requestedPersona: PersonaProvisioningPersona;
  requestedAccountType?: PersonaSignupAccountType;
  staffSignupCode?: string;
  schoolId?: string;
  financialAidInvite?: boolean;
  createdAt: number;
};

const PERSONA_SIGNUP_INTENT_KEY = 'ystnj_signup_intent_v1';
const MAX_INTENT_AGE_MS = 24 * 60 * 60 * 1000;

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const normalizeEmail = (value: unknown): string => normalizeText(value).toLowerCase();

const normalizeCode = (value: unknown): string => normalizeText(value);

const normalizeSchoolId = (value: unknown): string => normalizeText(value);

const isAccountType = (value: unknown): value is PersonaSignupAccountType =>
  value === 'parent' || value === 'staff';

const isProvisioningPersona = (value: unknown): value is PersonaProvisioningPersona =>
  value === 'parent' || value === 'staff' || value === 'administrator';

const isIntentFresh = (createdAt: number): boolean => {
  if (!Number.isFinite(createdAt) || createdAt <= 0) {
    return false;
  }
  return Date.now() - createdAt <= MAX_INTENT_AGE_MS;
};

const parseIntentPayload = (value: unknown): PersonaSignupIntent | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const payload = value as Record<string, unknown>;
  const email = normalizeEmail(payload.email);
  const requestedAccountType = isAccountType(payload.requestedAccountType)
    ? payload.requestedAccountType
    : undefined;
  const requestedPersona = isProvisioningPersona(payload.requestedPersona)
    ? payload.requestedPersona
    : requestedAccountType;
  const createdAt = Number(payload.createdAt);
  if (!requestedPersona || !isIntentFresh(createdAt)) {
    return null;
  }

  const staffSignupCode = normalizeCode(payload.staffSignupCode);
  const schoolId = normalizeSchoolId(payload.schoolId);
  return {
    version: 1,
    email: email || undefined,
    requestedPersona,
    requestedAccountType,
    staffSignupCode: staffSignupCode || undefined,
    schoolId: schoolId || undefined,
    financialAidInvite: payload.financialAidInvite === true || undefined,
    createdAt,
  };
};

const readStoredIntent = (storage: Storage): PersonaSignupIntent | null => {
  const raw = storage.getItem(PERSONA_SIGNUP_INTENT_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    const intent = parseIntentPayload(parsed);
    if (!intent) {
      storage.removeItem(PERSONA_SIGNUP_INTENT_KEY);
      return null;
    }
    return intent;
  } catch {
    storage.removeItem(PERSONA_SIGNUP_INTENT_KEY);
    return null;
  }
};

export const readPersonaSignupIntent = (): PersonaSignupIntent | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return readStoredIntent(window.sessionStorage) ?? readStoredIntent(window.localStorage);
};

export const readPersonaSignupIntentForEmail = (email: string | null | undefined): PersonaSignupIntent | null => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    return null;
  }
  const intent = readPersonaSignupIntent();
  if (!intent) {
    return null;
  }
  return !intent.email || intent.email === normalizedEmail ? intent : null;
};

export const persistPersonaSignupIntent = (payload: {
  email?: string | null;
  requestedPersona?: PersonaProvisioningPersona | null;
  requestedAccountType?: PersonaSignupAccountType | null;
  staffSignupCode?: string | null;
  schoolId?: string | null;
  financialAidInvite?: boolean | null;
}): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const requestedAccountType = isAccountType(payload.requestedAccountType)
    ? payload.requestedAccountType
    : undefined;
  const requestedPersona = isProvisioningPersona(payload.requestedPersona)
    ? payload.requestedPersona
    : requestedAccountType;
  if (!requestedPersona) {
    return;
  }
  const email = normalizeEmail(payload.email);
  const staffSignupCode = normalizeCode(payload.staffSignupCode);
  const schoolId = normalizeSchoolId(payload.schoolId);
  const intent: PersonaSignupIntent = {
    version: 1,
    email: email || undefined,
    requestedPersona,
    requestedAccountType,
    staffSignupCode: staffSignupCode || undefined,
    schoolId: schoolId || undefined,
    financialAidInvite: payload.financialAidInvite === true || undefined,
    createdAt: Date.now(),
  };
  if (email) {
    window.localStorage.setItem(PERSONA_SIGNUP_INTENT_KEY, JSON.stringify(intent));
    window.sessionStorage.removeItem(PERSONA_SIGNUP_INTENT_KEY);
    return;
  }
  window.sessionStorage.setItem(PERSONA_SIGNUP_INTENT_KEY, JSON.stringify(intent));
  window.localStorage.removeItem(PERSONA_SIGNUP_INTENT_KEY);
};

export const clearPersonaSignupIntent = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(PERSONA_SIGNUP_INTENT_KEY);
  window.sessionStorage.removeItem(PERSONA_SIGNUP_INTENT_KEY);
};

export const clearPersonaSignupIntentForEmail = (email: string | null | undefined): void => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    return;
  }
  const intent = readPersonaSignupIntent();
  if (!intent) {
    return;
  }
  if (!intent.email || intent.email === normalizedEmail) {
    clearPersonaSignupIntent();
  }
};
